import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, RefreshCw, Lightbulb } from 'lucide-react';

export default function RefundPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-purple-100 hover:text-white hover:bg-purple-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Refund Policy</h1>
          </div>
          <p className="text-purple-100">Last Updated: December 6, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="prose prose-sm max-w-none pt-6">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Overview</h2>
              <p className="text-gray-700 mb-4">
                At LexProcure PH, a proprietary application owned by <strong>SRAGCircle OPC</strong> and developed under its portfolio brand <strong>LexDoRight</strong>, we strive to provide exceptional value through our legal research platform. This Refund Policy 
                explains our approach to refunds, cancellations, and subscription management.
              </p>
              <p className="text-gray-700">
                We encourage all users to take advantage of our <strong>10-day free trial</strong> to fully evaluate the Service 
                before making a purchase decision.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Free Trial Period</h2>
              <p className="text-gray-700 mb-4">
                All new users receive a <strong>10-day free trial</strong> with full access to all features. This trial period allows you to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Explore all legal resources and documents</li>
                <li>Test the search and AI-powered tools</li>
                <li>Create bookmarks, notes, and highlights</li>
                <li>Use the Mode Selection Wizard and other Zap Tools</li>
                <li>Evaluate if the Service meets your needs</li>
              </ul>
              <p className="text-gray-700">
                <strong>No payment is required during the trial period.</strong> You can cancel anytime during the trial without any charges.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Refund Eligibility</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.1 7-Day Money-Back Guarantee</h3>
              <p className="text-gray-700 mb-4">
                If you are not satisfied with your paid subscription, you may request a <strong>full refund within 7 days</strong> 
                of your payment date, provided:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>The refund request is made within 7 calendar days of payment</li>
                <li>This is your first subscription payment (not a renewal)</li>
                <li>You have not violated our Terms and Conditions</li>
                <li>Your account has not been banned or suspended</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.2 Non-Refundable Situations</h3>
              <p className="text-gray-700 mb-4">
                Refunds are <strong>NOT</strong> available in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>More than 7 days have passed since payment</li>
                <li>Renewal subscriptions (only first-time purchases are eligible)</li>
                <li>Partial subscription periods (we do not offer prorated refunds)</li>
                <li>Change of mind after the 7-day period</li>
                <li>Failure to use the Service</li>
                <li>Accounts banned for Terms violations</li>
                <li>Promotional or discounted subscriptions (unless otherwise stated)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.3 Technical Issues</h3>
              <p className="text-gray-700">
                If you experience technical problems preventing you from using the Service, please contact us at 
                <strong> admin@lexdoright.com</strong> before requesting a refund. We will make reasonable efforts to resolve 
                technical issues. Refunds for technical problems will be considered on a case-by-case basis.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. How to Request a Refund</h2>
              <p className="text-gray-700 mb-4">
                To request a refund, follow these steps:
              </p>
              <ol className="list-decimal pl-6 text-gray-700 mb-4">
                <li className="mb-2">
                  <strong>Send an email</strong> to admin@lexdoright.com with the subject line "Refund Request"
                </li>
                <li className="mb-2">
                  <strong>Include the following information:</strong>
                  <ul className="list-disc pl-6 mt-2">
                    <li>Your registered email address</li>
                    <li>Full name</li>
                    <li>Payment date and amount</li>
                    <li>Payment reference number (if available)</li>
                    <li>Reason for refund request</li>
                  </ul>
                </li>
                <li className="mb-2">
                  <strong>Wait for our response</strong> within 2-3 business days
                </li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Refund Processing</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 Review Period</h3>
              <p className="text-gray-700 mb-4">
                We will review your refund request within <strong>2-3 business days</strong>. We may contact you for additional 
                information if needed.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Approval and Processing</h3>
              <p className="text-gray-700 mb-4">
                If your refund is approved:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>You will receive a confirmation email</li>
                <li>Your account will be immediately downgraded to expired status</li>
                <li>You will lose all access to the Service, including the legal library, tools, and features</li>
                <li>Refund will be processed within 5-7 business days</li>
                <li>The refund will be sent to your original payment method</li>
              </ul>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                <p className="text-amber-900 font-semibold mb-2">⚠️ Important: Access Termination</p>
                <p className="text-amber-800 text-sm">
                  By requesting and accepting a refund, you acknowledge and agree that:
                </p>
                <ul className="list-disc pl-6 text-amber-800 text-sm mt-2">
                  <li>Your access to LexProcure PH will be immediately terminated upon refund approval</li>
                  <li>Any attempt to access the application or its content after refund constitutes unauthorized use</li>
                  <li>Unauthorized use of the Service and related materials is a violation of our Terms and Conditions</li>
                  <li>We reserve the right to take legal action against unauthorized access or use</li>
                </ul>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.3 Refund Timeline by Payment Method</h3>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <ul className="list-disc pl-6 text-gray-700">
                  <li><strong>GCash:</strong> 5-7 business days</li>
                  <li><strong>Maya:</strong> 5-7 business days</li>
                  <li><strong>Bank Transfer:</strong> 7-10 business days</li>
                  <li><strong>Credit/Debit Card:</strong> 7-14 business days (depending on bank)</li>
                </ul>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.4 Refund Denial</h3>
              <p className="text-gray-700">
                If your refund request is denied, we will provide a clear explanation. You may appeal the decision by 
                providing additional information or context.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Cancellation Policy</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.1 During Trial Period</h3>
              <p className="text-gray-700 mb-4">
                You can cancel your account anytime during the 10-day trial period without any charges. Simply stop using 
                the Service, and your account will automatically expire after the trial.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.2 After Payment</h3>
              <p className="text-gray-700 mb-4">
                If you wish to cancel your subscription:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Contact us at admin@lexdoright.com</li>
                <li>Your subscription will remain active until the end of the paid period</li>
                <li>No refund will be provided for the remaining subscription time (except within the 7-day refund window)</li>
                <li>You can continue using the Service until expiration</li>
                <li>No automatic renewal - you must manually renew after expiration</li>
              </ul>
              
              <p className="text-gray-700 font-semibold mt-4">
                If you request a refund within the 7-day window, your access will be immediately terminated upon refund approval, 
                and continued use will be considered unauthorized.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.3 Account Deletion</h3>
              <p className="text-gray-700">
                If you wish to permanently delete your account and data, email us at admin@lexdoright.com. 
                Account deletion does not automatically qualify for a refund unless within the 7-day window.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Subscription Renewals</h2>
              <p className="text-gray-700 mb-4">
                LexProcure PH does <strong>NOT</strong> have automatic renewals. When your subscription expires:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>You will receive email reminders before expiration</li>
                <li>Your account will be downgraded to expired status after the subscription period</li>
                <li>You can manually renew anytime by making a new payment</li>
                <li>No charges will occur without your explicit action</li>
              </ul>
              <p className="text-gray-700">
                <strong>Annual Renewal Rate:</strong> ₱99.00 per year
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Special Circumstances</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.1 Service Disruption</h3>
              <p className="text-gray-700 mb-4">
                If LexProcure PH experiences extended service disruption (more than 48 consecutive hours), we may offer:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Subscription extension equal to the downtime</li>
                <li>Partial refund for affected period</li>
                <li>Account credit for future use</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.2 Promotional Offers</h3>
              <p className="text-gray-700 mb-4">
                Subscriptions purchased with promotional codes or discounts may have different refund terms, which will 
                be clearly stated at the time of purchase.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.3 Bulk/Institutional Subscriptions</h3>
              <p className="text-gray-700">
                Organizations purchasing multiple subscriptions should contact us at admin@lexdoright.com for custom 
                refund and cancellation terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Chargebacks and Disputes</h2>
              <p className="text-gray-700 mb-4">
                If you initiate a chargeback or payment dispute through your bank/payment provider:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your account will be immediately suspended pending investigation</li>
                <li>Please contact us first to resolve issues - chargebacks incur additional processing fees</li>
                <li>Fraudulent chargebacks may result in permanent account termination</li>
                <li>We will provide documentation to payment providers as needed</li>
              </ul>
              <p className="text-gray-700">
                <strong>We encourage direct communication</strong> to resolve payment issues before initiating chargebacks.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Changes to Refund Policy</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify this Refund Policy at any time. Changes will:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Be posted on this page with an updated "Last Updated" date</li>
                <li>Not affect existing subscriptions unless required by law</li>
                <li>Apply to new purchases made after the effective date</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For refund requests, cancellations, or questions about this policy:
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Company:</strong> SRAGCircle OPC (via LexDoRight)</p>
                <p className="text-gray-700"><strong>Email:</strong> admin@lexdoright.com</p>
                <p className="text-gray-700"><strong>Website:</strong> lexdoright.com</p>
                <p className="text-gray-700 mt-2"><strong>Response Time:</strong> 2-3 business days</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded">
              <p className="text-sm text-emerald-800 font-semibold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 flex-shrink-0" />
                Pro Tip: Maximize Your Trial Period
              </p>
              <p className="text-sm text-emerald-800">
                We recommend thoroughly testing all features during your 10-day free trial. This ensures you make an 
                informed decision before purchasing. If you need help or have questions during the trial, contact us at 
                admin@lexdoright.com - we're here to help!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
